import { PROJECT_KIND_LABELS } from '@fos/shared/labels';
import { FolderKanban } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatDateShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { isProjectOverdue, projectHoursText } from '../project-format';
import { ProjectStatusLabel } from './ProjectBits';

/**
 * Wiersze projektów klienta w profilu klienta (Figma 05 Profil REA, sekcja „Projekty i usługi”):
 * „nazwa · projekt · 32 / 40 h · do 30.10 · status”, wiersz 48 px z linią pod spodem, klik = szczegóły projektu.
 * Nagłówek sekcji i wiersze usług dokłada profil klienta. Bez modułu Projekty komponent nic nie pokazuje.
 * Osoba w trybie częściowym (dostęp tylko do projektu klienta) widzi wyłącznie swoje projekty.
 */
export function ClientProjectsRows({
  clientId,
  includeClosed = false,
  showEmpty = true,
  className,
}: {
  clientId: string;
  /** dołącz zakończone i anulowane projekty */
  includeClosed?: boolean;
  /** pusty stan „Brak projektów” (wyłącz, gdy sekcja ma też usługi) */
  showEmpty?: boolean;
  className?: string;
}) {
  const access = useAccess();
  const enabled = access.can('projects');
  const query = trpc.projects.listByClient.useQuery({ clientId, includeClosed }, { enabled });

  if (!enabled) return null;
  if (query.isPending) {
    return (
      <div role="status" aria-label="Wczytywanie projektów" className={className}>
        {[0, 1].map((i) => (
          <div key={i} className="flex h-12 items-center gap-3 border-b border-line">
            <Skeleton className={cn('h-3.5', i ? 'w-1/3' : 'w-2/5')} />
            <span className="flex-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        compact
        title="Nie udało się wczytać projektów"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
        className={className}
      />
    );
  }
  if (query.data.length === 0) {
    return showEmpty ? (
      <EmptyState compact icon={FolderKanban} title="Brak otwartych projektów" className={className} />
    ) : null;
  }

  return (
    <ul className={className} aria-label="Projekty klienta">
      {query.data.map((p) => {
        const hours = projectHoursText(p.usedSeconds, p.hoursBudgetSeconds);
        const due = p.endDate ? `do ${formatDateShort(p.endDate)}` : null;
        const overdue = isProjectOverdue(p);
        return (
          <li key={p.id}>
            <Link
              to={`/projekty/${p.id}`}
              className={cn(
                'flex min-h-12 items-center gap-3 border-b border-line py-2 transition-colors hover:bg-surface',
                'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
              )}
            >
              <span className="min-w-0 truncate text-body-s font-medium text-fg">{p.name}</span>
              <span className="shrink-0 text-caption text-fg-muted">{PROJECT_KIND_LABELS.project}</span>
              <span className="flex-1" />
              {(hours || due) && (
                <span className="hidden min-w-0 shrink-[100] truncate text-caption whitespace-nowrap text-fg-secondary sm:inline">
                  {hours}
                  {hours && due ? ' · ' : null}
                  {due && <span className={cn(overdue && 'text-danger')}>{due}</span>}
                </span>
              )}
              <ProjectStatusLabel project={p} className="shrink-0" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
